import { ReadonlyVec3 } from 'gl-matrix';
import { Palette, Theme } from '@mui/material';

/**
 * Converts RGB values (0-1 range) to CSS rgb string
 */
export function rgbToString(r: number, g: number, b: number): string {
    return `rgb(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)})`;
}

/**
 * Converts a normalized RGB vec3 ([0–1] per channel)
 * to a CSS hex color string (e.g. '#00ffff').
 */
export function vec3ToHex(v: ReadonlyVec3): string {
    const r = Math.round(v[0] * 255);
    const g = Math.round(v[1] * 255);
    const b = Math.round(v[2] * 255);
    return (
        '#' +
        r.toString(16).padStart(2, '0') +
        g.toString(16).padStart(2, '0') +
        b.toString(16).padStart(2, '0')
    );
}

/**
 * Converts hex or rgb color to rgba with alpha channel
 */
export function toRgba(color: string, alpha: number): string {
    if (typeof color !== 'string') {
        return color;
    }

    // Handle hex colors
    if (color.startsWith('#')) {
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    // Handle rgb/rgba colors
    if (color.startsWith('rgb')) {
        const match = color.match(/\d+/g);
        if (match && match.length >= 3) {
            return `rgba(${match[0]}, ${match[1]}, ${match[2]}, ${alpha})`;
        }
    }

    return color;
}

/**
 * Gets the actual color value from MUI theme palette
 */
export function resolveThemeColor(colorValue: string, theme: Theme): string {
    const paletteKey = colorValue.split('.')[0];
    const paletteColor = theme.palette[paletteKey as keyof Palette];

    if (
        paletteColor &&
        typeof paletteColor === 'object' &&
        'main' in paletteColor
    ) {
        return (paletteColor as { main: string }).main;
    }

    return colorValue;
}
